import { getUserSetting } from "../../../common/user-setting"
import type {
  DiagnosticsActivityLike,
  CheckboxProps,
  PlanLessonController,
  PlanLessonActivity,
  DiagnosticsTopics,
  SessionLike,
} from "./PlanLessonTypes"

interface CheckboxParams {
  controller: PlanLessonController
  isPlanningSession: boolean
  sessionData: SessionLike
  activity: PlanLessonActivity
  enrolment?: any
}

function parseNumericValue(value: unknown): number {
  const parsedValue = parseInt(String(value ?? 0), 10)
  return Number.isNaN(parsedValue) ? 0 : parsedValue
}

export function isSessionCompleted(
  session: { ses_is_completed?: number } | null | undefined
): boolean {
  return !!session && session.ses_is_completed === 1
}

export const userSetting = getUserSetting()

export function getCheckboxProps({
  controller,
  isPlanningSession,
  sessionData,
  activity,
  enrolment,
}: CheckboxParams): CheckboxProps | undefined {
  if (isPlanningSession) {
    return undefined
  }

  return {
    checkable: !!sessionData?.session,
    checked: (controller.selectedActivityKeys || []).includes(
      activity.pk_lesson_activity_key
    ),
    onCheckedChanged: () =>
      controller.handleCheckboxActivityChange(
        activity.pk_lesson_activity_key,
        activity,
        enrolment?.tbl_student?.Lesson?.LessonActivities?.length || 0
      ),
  }
}

export function getUniqueTopicsNames(
  topics: DiagnosticsTopics | undefined
): string {
  const uniqueTopicsNames = new Set<string>()

  if (topics && Array.isArray(topics)) {
    topics.forEach(subject => {
      ;(subject || []).forEach(data => {
        if (data?.topic_name != null) {
          uniqueTopicsNames.add(data.topic_name)
        }
      })
    })
  }

  return Array.from(uniqueTopicsNames).join(", ")
}

export function getDiagnosticsStats(activity: DiagnosticsActivityLike): {
  totalCoins: number
  totalQuestion: number
  countAnswer: number
  questionStates: Array<number | null>
} {
  let totalCoins = 0
  let totalQuestion = 0
  let countAnswer = 0
  const questionStates: Array<number | null> = []

  ;(activity?.LessonActivitiesQuestions || []).forEach(activityQuestion => {
    let score = 0

    const items =
      Number(activity?.fk_sub_id) === 1
        ? activityQuestion?.english_question || []
        : activityQuestion?.question || []

    items.forEach(question => {
      totalQuestion += 1
      totalCoins += 2

      const attempts = question?.StudentAnsAttempts || []
      if (attempts.length > 0) {
        countAnswer += 1
      }

      attempts.forEach(answerAttemptQuestion => {
        score += parseNumericValue(answerAttemptQuestion?.points_obtained)
      })
    })

    questionStates.push(Number.isNaN(score) ? null : score)
  })

  return { totalCoins, totalQuestion, countAnswer, questionStates }
}

export function getDiagnosticHighlightClass(
  sessionData: SessionLike,
  activity: DiagnosticsActivityLike
): string {
  if (sessionData?.session?.ses_is_completed !== 1) {
    return ""
  }

  if (activity.status == 1) {
    const score = parseNumericValue(activity.mastryScore)

    if (score >= 75) {
      return "activity-card-success"
    }

    return "activity-card-blue"
  }

  return "activity-card-danger"
}

export function getDiagnosticsStatusMeta(status: unknown): {
  className: string
  label: string | null
} {
  const numericStatus = Number(status)

  if (numericStatus === 0) {
    return {
      className: "text-danger",
      label: "Incomplete",
    }
  }

  if (numericStatus === 1) {
    return {
      className: "text-success",
      label: "Completed",
    }
  }

  return {
    className: "",
    label: null,
  }
}

export function getDiagnosticsAttemptedText(
  countAnswer: number,
  totalQuestion: number
): string {
  return `${countAnswer}/${totalQuestion} attempted`
}

export function getDiagnosticsScoreText(
  sessionData: SessionLike,
  activity: DiagnosticsActivityLike
): string | null {
  if (sessionData?.session?.ses_is_completed !== 1) {
    return null
  }

  return activity.mastryScore ? `Score: ${activity.mastryScore}` : "Score: 0"
}

export function getDiagnosticsLevelPrefix(levelName: unknown): string | null {
  return levelName && String(levelName) !== "" ? `${levelName} - ` : null
}

export function getQuestionBadgeClass(score: number | null): string {
  if (score === 1) {
    return "attempt2 text-warning bg-success-subtle"
  }

  if (score === 2) {
    return "attempt1 custom-success bg-success-subtle"
  }

  if (score === 0) {
    return "attempt3 cs-txt-danger bg-danger-subtle"
  }

  return ""
}

